import * as yup from "yup";
import {
  fieldsValidation,
  formatNumber,
  getRawNumberValue,
} from "core/utils/localizationUtils";

// Validation schema for Change Order form
export const addChangeOrderValidationSchema = yup.object({
  changeOrderDate: yup
    .date()
    .required("Change Order Date is required")
    .typeError("Enter a valid Change Order Date"),
  clientName: yup
    .string("Enter Client Name")
    .required("Client Name is required"),
  clientEmail: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  clientMobile: yup.string().nullable(),
  clientCompanyId: yup.number().nullable(),
  clientType: yup.string().nullable(),
  projectId: yup.number().nullable(),
  propertyId: yup.number().nullable(),
  initiatedBy: yup.string().nullable(),
  // changeOrderStatusId: yup.string().required("Status is required"),
  changesRequired: yup
    .string("Enter Changes Required")
    .required("Changes Required is required"),
  reasonForChange: yup
    .string("Enter Reason For Change")
    .required("Reason For Change is required"),
  originalContractCost: fieldsValidation
    .number("Original Cost")
    .min(0, "Must be >= 0")
    .typeError("Enter a valid Original Contract Cost")
    .nullable(),
  estimatedChangeCost: fieldsValidation
    .number("Estimated Cost")
    .min(0, "Must be >= 0")
    .typeError("Enter a valid Estimated Change Cost")
    .nullable(),
  originalCompletionDate: yup
    .date()
    .typeError("Enter a valid Original Completion Date")
    .nullable(),
  revisedCompletionDate: yup
    .date()
    .typeError("Enter a valid Revised Completion Date")
    .nullable(),
  notes: yup.string().max(1000, "Max 1000 characters").nullable(),
  jobId: yup
    .string()
    .nullable()
    .when("clientType", {
      is: "contractor",
      then: (schema) =>
        schema
          .required("Job Id is mandatory for creating Change Orders")
          .min(1),
      otherwise: (schema) => schema.nullable(),
    }),
});

// Field configuration similar to invoice form config
export const addChangeOrderFields = [
  {
    key: "clientName",
    label: "Client Name",
    placeholder: "Enter / Select Client",
    type: "select",
    dynamicField: true,
    creatable: true,
    required: true,
    width: "33%",
    options: [],
  },
  {
    key: "clientEmail",
    label: "Client Email",
    placeholder: "Enter Email",
    type: "text",
    required: true,
    width: "33%",
  },
  {
    key: "clientMobile",
    label: "Client Mobile",
    placeholder: "Enter Mobile Number",
    type: "tel",
    // required: true,
    width: "33%",
  },
  {
    key: "changeOrderDate",
    label: "Change Order Date",
    placeholder: "MM/DD/YYYY",
    type: "dateField",
    required: true,
    width: "33%",
  },
  {
    key: "originalCompletionDate",
    label: "Original Completion Date",
    placeholder: "MM/DD/YYYY",
    type: "dateField",
    width: "33%",
  },
  {
    key: "revisedCompletionDate",
    label: "Revised Completion Date",
    placeholder: "MM/DD/YYYY",
    type: "dateField",
    width: "33%",
  },
  {
    key: "originalContractCost",
    label: "Original Contract Cost ($)",
    placeholder: "0.00",
    type: "text",
    width: "33%",
    customValue: formatNumber,
    extraInputProps: (formik) => ({
      onChange: (e) => {
        const validValue = getRawNumberValue(e.target.value);
        formik.setFieldValue("originalContractCost", validValue);
      },
    }),
  },
  {
    key: "estimatedChangeCost",
    label: "Estimated Change Cost ($)",
    placeholder: "0.00",
    type: "text",
    width: "33%",
    customValue: formatNumber,
    extraInputProps: (formik) => ({
      onChange: (e) => {
        const validValue = getRawNumberValue(e.target.value);
        formik.setFieldValue("estimatedChangeCost", validValue);
      },
    }),
  },
  // {
  //   key: "changeOrderStatusId",
  //   label: "Status",
  //   placeholder: "Select Status",
  //   required: true,
  //   type: "select",
  //   select: true,
  //   dynamicField: true,
  //   width: "33%",
  // },
  {
    key: "jobId",
    label: "Job",
    placeholder: "Select Job",
    type: "select",
    width: "33%",
    dynamicField: true,
  },
  {
    key: "changesRequired",
    label: "Changes Required",
    placeholder: "Describe the changes required",
    type: "textArea",
    required: true,
    extraInputProps: {
      multiline: true,
      minRows: 3,
      className: "registration-formField task-notes property-details-textarea",
    },
    width: "100%",
  },
  {
    key: "reasonForChange",
    label: "Reason For Change",
    placeholder: "Describe the reason for change",
    type: "textArea",
    required: true,
    extraInputProps: {
      multiline: true,
      minRows: 3,
      className: "registration-formField task-notes property-details-textarea",
    },
    width: "100%",
  },
  {
    key: "notes",
    label: "Notes",
    placeholder: "Optional notes (max 1000 chars)",
    type: "textArea",
    extraInputProps: {
      multiline: true,
      minRows: 2,
      className: "registration-formField task-notes property-details-textarea",
    },
    width: "100%",
  },
];

export const addChangeOrderForm = {
  formFields: addChangeOrderFields,
  validationSchema: addChangeOrderValidationSchema,
  defaultState: (defaultState) => {
    if (defaultState) return defaultState;
    return Object.fromEntries(
      Object.keys(addChangeOrderValidationSchema.fields).map((key) => [key, ""])
    );
  },
};

export const createChangeOrderFieldExtraProps = {
  clientName: {
    builder: () => ({
      optionKey: "userId",
      onChange: ({ field, value, inputValue, formik }) => {
        formik.setFieldValue(field.key, inputValue || value?.text || "");
        formik.setFieldValue(
          "clientEmail",
          value?.data?.email || value?.data?.createdBy || ""
        );
        formik.setFieldValue(
          "clientMobile",
          value?.data?.mobileNumber ? value.data.mobileNumber : ""
        );
        // formik.setFieldTouched("clientEmail", true);
        formik.setFieldValue("userId", value?.key || "");
        if (formik.values?.clientType === "Supplier")
          formik.setFieldValue(
            "clientCompanyId",
            value?.data?.companyId || null
          );
      },
    }),
    supplier: () => ({
      disabled: true,
    }),
  },
  jobId: {
    builder: (formik) => ({
      optionKey: "jobId",
      onChange: (field, value) => {
        formik.setFieldValue("jobId", value?.key || "");
        if (value?.data?.jobName)
          formik.setFieldValue("jobName", value.data.jobName);
      },
    }),
    supplier: (formik) => ({
      optionKey: "jobId",
      required: true,
      onChange: (field, value) => {
        formik.setFieldValue("jobId", value?.key || "");
        if (value?.data?.jobName)
          formik.setFieldValue("jobName", value.data.jobName);
      },
    }),
  },
};
